import { makeStyles } from "@material-ui/core";

const C = {
  bg: "#f5f7fb",
  card: "#ffffff",
  border: "#e5e9f0",
  line: "#eef1f5",
  slate: "#1e293b",
  slate2: "#334155",
  text: "#1f2933",
  muted: "#6b7280",
  danger: "#dc2626",
  dangerSoft: "#fef2f2",
  primary: "#2563eb",
  primarySoft: "#dbeafe",
  amber: "#f59e0b",
  amberSoft: "#fef3c7",
};

const useHolidayStyle = makeStyles(() => ({
  modalBox: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "94%",
    maxWidth: 1080,
    maxHeight: "92vh",
    background: C.card,
    borderRadius: 14,
    boxShadow: "0 24px 64px rgba(0,0,0,.35)",
    outline: "none",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    fontFamily: "'Inter', 'Segoe UI', Roboto, Arial, sans-serif",
  },

  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 24px",
    background: C.slate,
    color: "#fff",

    "& h2": {
      margin: 0,
      fontSize: 20,
      fontWeight: 800,
      color: "#fff",
      letterSpacing: "-0.01em",
    },
    "& .subtitle": {
      display: "block",
      fontSize: 12,
      color: "#94a3b8",
      fontWeight: 500,
      marginTop: 2,
      maxWidth: 640,
    },
    "& .closeBtn": {
      color: "#cbd5e1",
      "&:hover": { background: "rgba(255,255,255,.08)", color: "#fff" },
    },
  },

  body: {
    display: "grid",
    gridTemplateColumns: "1fr 340px",
    gap: 0,
    flex: 1,
    overflow: "hidden",
    background: "#fafbfc",
  },

  // ── LADO ESQUERDO: calendário ────────────────────────────────────────
  calendarSide: {
    padding: 20,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    "&::-webkit-scrollbar": { width: 8 },
    "&::-webkit-scrollbar-thumb": { background: "#cbd5e1", borderRadius: 4 },
  },

  calendarToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginBottom: 14,
  },

  monthLabel: {
    fontSize: 18,
    fontWeight: 800,
    color: C.slate,
    minWidth: 220,
    textAlign: "center",
    textTransform: "capitalize",
    letterSpacing: "-0.01em",
  },

  loadingHint: {
    marginLeft: 12,
    fontSize: 11,
    color: C.muted,
    fontStyle: "italic",
  },

  weekHeader: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 6,
    marginBottom: 6,
    "& span": {
      fontSize: 11,
      fontWeight: 800,
      color: C.muted,
      textAlign: "center",
      textTransform: "uppercase",
      letterSpacing: ".08em",
      padding: "6px 0",
    },
  },

  calendarGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 6,
  },

  dayCell: {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "8px 6px",
    minHeight: 78,
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    textAlign: "left",
    fontFamily: "inherit",
    transition: "all .12s",
    position: "relative",

    "& .dayNum": {
      fontSize: 14,
      fontWeight: 700,
      color: C.slate,
      marginBottom: 4,
    },
    "& .badges": {
      display: "flex",
      flexDirection: "column",
      gap: 2,
      width: "100%",
      overflow: "hidden",
    },
    "& .badge": {
      fontSize: 9,
      fontWeight: 700,
      padding: "2px 6px",
      borderRadius: 4,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      textAlign: "left",
      "&.more": {
        background: C.line,
        color: C.muted,
      },
    },

    "&:hover": {
      background: C.primarySoft,
      borderColor: C.primary,
      transform: "translateY(-1px)",
      boxShadow: "0 4px 10px rgba(37,99,235,.15)",
    },

    "&.outside": {
      background: "#f9fafb",
      "& .dayNum": { color: "#cbd5e1" },
      "&:hover": {
        background: "#f3f4f6",
        borderColor: C.border,
        transform: "none",
        boxShadow: "none",
      },
    },

    "&.today": {
      borderColor: C.primary,
      borderWidth: 2,
      "& .dayNum": {
        color: C.primary,
        fontSize: 15,
        fontWeight: 800,
      },
    },

    "&.hasHoliday": {
      background: C.amberSoft,
      borderColor: C.amber,
    },

    "&.selected": {
      background: C.slate,
      borderColor: C.slate,
      "& .dayNum": { color: "#fff" },
      "& .badge": {
        background: "rgba(255,255,255,.15) !important",
        color: "#fff !important",
      },
    },
  },

  legend: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 16,
    padding: "10px 12px",
    background: C.card,
    borderRadius: 8,
    border: `1px solid ${C.border}`,

    "& .legendItem": {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 11,
      color: C.slate2,
      fontWeight: 600,
    },
    "& .swatch": {
      display: "inline-block",
      width: 12,
      height: 12,
      borderRadius: 3,
      border: "2px solid",
    },
  },

  // ── LADO DIREITO: form ───────────────────────────────────────────────
  formSide: {
    padding: 20,
    borderLeft: `1px solid ${C.border}`,
    background: C.card,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",

    "&::-webkit-scrollbar": { width: 8 },
    "&::-webkit-scrollbar-thumb": { background: "#cbd5e1", borderRadius: 4 },
  },

  formHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "10px 14px",
    background: C.line,
    borderRadius: 8,
    marginBottom: 4,
  },

  formLabel: {
    fontSize: 10,
    fontWeight: 800,
    color: C.muted,
    textTransform: "uppercase",
    letterSpacing: ".08em",
  },

  formDate: {
    fontSize: 14,
    fontWeight: 700,
    color: C.slate,
    textTransform: "capitalize",
    marginTop: 2,
  },

  deleteBtn: {
    color: C.danger,
    "&:hover": {
      background: C.dangerSoft,
    },
  },

  formActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 16,
    paddingTop: 16,
    borderTop: `1px solid ${C.border}`,
  },

  cancelBtn: {
    padding: "6px 16px",
    borderRadius: 8,
    color: C.muted,
    fontWeight: 600,
    textTransform: "none",
    fontSize: 13,
    "&:hover": { background: C.line },
  },

  submitBtn: {
    background: C.slate,
    color: "#fff",
    padding: "6px 20px",
    borderRadius: 8,
    fontWeight: 700,
    textTransform: "none",
    fontSize: 13,
    "&:hover": { background: "#0f172a" },
    "&.Mui-disabled": { background: "#94a3b8", color: "#fff" },
  },

  // ── LADO DIREITO: hint (nenhum dia selecionado) ──────────────────────
  hintSide: {
    padding: 40,
    borderLeft: `1px solid ${C.border}`,
    background: C.card,
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },

  hintIcon: {
    fontSize: 48,
    marginBottom: 12,
    opacity: 0.6,
  },

  hintTitle: {
    fontSize: 16,
    fontWeight: 800,
    color: C.slate,
    marginBottom: 6,
  },

  hintText: {
    fontSize: 13,
    color: C.muted,
    lineHeight: 1.5,
    maxWidth: 240,
  },
}));

export default useHolidayStyle;
